import { useEffect, useRef } from 'react';

interface AuroraOverlayProps {
  colors?: {
    primary?: string;
    secondary?: string;
  };
  opacity?: number;
}

export default function AuroraOverlay({ colors, opacity = 0.25 }: AuroraOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let time = 0;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Parse CSS color string (hex or rgb/rgba) to [r,g,b]
    const parseCssColorToRgb = (input: string): [number, number, number] => {
      let c = input.trim();
      if (!c) return [10, 116, 255];
      const rgbMatch = c.match(/^rgba?\(([^)]+)\)$/i);
      if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map(v => parseFloat(v.trim()));
        return [
          Math.max(0, Math.min(255, parts[0] || 0)),
          Math.max(0, Math.min(255, parts[1] || 0)),
          Math.max(0, Math.min(255, parts[2] || 0)),
        ];
      }
      if (c.startsWith('#')) c = c.slice(1);
      if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
      if (c.length !== 6) return [10, 116, 255];
      const r = parseInt(c.slice(0,2), 16);
      const g = parseInt(c.slice(2,4), 16);
      const b = parseInt(c.slice(4,6), 16);
      return [r,g,b];
    };

    const drawAurora = (yBase: number, amplitude: number, frequency: number, speed: number, color1: string, color2: string) => {
      const gradient = ctx.createLinearGradient(0, yBase - amplitude, 0, yBase + amplitude * 3);
      const [r1,g1,b1] = parseCssColorToRgb(color1);
      const [r2,g2,b2] = parseCssColorToRgb(color2);
      gradient.addColorStop(0, `rgba(${r1},${g1},${b1},0.0)`);
      gradient.addColorStop(0.3, `rgba(${r1},${g1},${b1},0.25)`);
      gradient.addColorStop(0.6, `rgba(${r2},${g2},${b2},0.1875)`);
      gradient.addColorStop(1, `rgba(${r2},${g2},${b2},0.0)`);

      ctx.fillStyle = gradient;

      ctx.beginPath();
      ctx.moveTo(0, yBase);

      for (let x = 0; x <= canvas.width; x += 5) {
        const y1 = Math.sin((x * frequency) + (time * speed)) * amplitude + yBase;
        const y2 = Math.sin((x * frequency * 1.3) + (time * speed * 0.8)) * (amplitude * 0.7) + yBase;
        const y = (y1 + y2) / 2;
        ctx.lineTo(x, y);
      }

      ctx.lineTo(canvas.width, canvas.height);
      ctx.lineTo(0, canvas.height);
      ctx.closePath();
      ctx.fill();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Read header accent each frame to respond to theme/accent changes
      const accent = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-header-accent')
        .trim() || '#0A74FF';
      const color1 = colors?.primary || accent;
      const color2 = colors?.secondary || accent;

      drawAurora(canvas.height * 0.3, 80, 0.005, 0.01, color1, color2);
      drawAurora(canvas.height * 0.4, 100, 0.007, 0.015, color2, color1);
      drawAurora(canvas.height * 0.5, 60, 0.006, 0.012, color1, color2);

      time += 0.3;
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [colors, opacity]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -1,
        opacity: opacity
      }}
    />
  );
}
